import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

// `?debug=1` only. The TV's browser (webOS Signage, Chromium 79, "Play via URL") does
// not render exactly what a MacBook does, and nobody can open devtools on it. This
// overlay puts the facts a photo from the counter can carry onto the wall: the real
// viewport and pixel ratio, whether the brand fonts loaded, which program is up and how
// old the data is, a 200 px scale bar, and the exact edge of the 1920×1080 canvas so
// overscan or cropping shows as a missing frame. Nothing here is ever on the customer's
// wall — the boot URL has no `?debug`.

function fontStatus(): string {
  if (!('fonts' in document)) return 'font API missing'
  const check = (spec: string) => (document.fonts.check(spec) ? 'ok' : 'MISSING')
  return (
    'Losta Masta 700 ' + check('700 40px "Losta Masta"') +
    ' · URW 400 ' + check('400 24px "URW Geometric"') +
    ' · URW 700 ' + check('700 24px "URW Geometric"')
  )
}

// Flexbox `gap` is Chrome 84+; the wall is Chromium 79. The boards no longer depend on it
// (chrome79css.test.ts), this line just keeps the fact visible in the photo.
function flexGapSupport(): string {
  const probe = document.createElement('div')
  probe.style.display = 'flex'
  probe.style.flexDirection = 'column'
  probe.style.rowGap = '1px'
  probe.appendChild(document.createElement('div'))
  probe.appendChild(document.createElement('div'))
  document.body.appendChild(probe)
  const ok = probe.scrollHeight === 1
  document.body.removeChild(probe)
  return ok ? 'yes' : 'NO'
}

function hhmmss(ms: number | null): string {
  if (ms === null) return 'none yet'
  const d = new Date(ms)
  const two = (n: number) => ('0' + n).slice(-2)
  return two(d.getHours()) + ':' + two(d.getMinutes()) + ':' + two(d.getSeconds())
}

export function DebugOverlay({ program, lastSuccessAt }: { program: string; lastSuccessAt: number | null }) {
  const search = useLocation().search
  const on = search.indexOf('debug=1') !== -1
  // A face is only requested once text uses it, and the overlay mounts before the catalog
  // arrives — so a check made on the first render (or on `fonts.ready`, which resolves
  // while nothing is loading yet) reports Losta Masta as missing although it lands a
  // moment later. Draw again every time the browser finishes a batch of font loads.
  const [, redraw] = useState(0)
  useEffect(() => {
    if (!on || !('fonts' in document)) return undefined
    const bump = () => redraw((n) => n + 1)
    document.fonts.addEventListener('loadingdone', bump)
    return () => document.fonts.removeEventListener('loadingdone', bump)
  }, [on])
  if (!on) return null

  const lines = [
    'viewport ' + window.innerWidth + '×' + window.innerHeight +
      ' · dpr ' + window.devicePixelRatio +
      ' · screen ' + window.screen.width + '×' + window.screen.height +
      ' · canvas 1920×1080',
    fontStatus() + ' · flex gap ' + flexGapSupport(),
    'program ' + program + ' · data ' + hhmmss(lastSuccessAt) + ' · now ' + hhmmss(Date.now()),
    navigator.userAgent.slice(0, 140),
  ]

  return (
    <>
      {/* The canvas edge: if the TV crops or overscans, part of this frame is gone. */}
      <div className="fixed left-0 top-0 w-[1920px] h-[1080px] border-[3px] border-brand-jasper pointer-events-none" />
      <div className="fixed left-[24px] bottom-[24px] max-w-[1200px] px-[16px] py-[12px] bg-white/90 text-brand-ink rounded-[8px] font-secondary font-normal text-[22px] leading-[1.3] pointer-events-none">
        {lines.map((l, i) => (
          <div key={i} className="whitespace-nowrap overflow-hidden text-ellipsis">
            {l}
          </div>
        ))}
        {/* 200 px bar with 50 px ticks: on the panel it must span 200/1920 of the width. */}
        <div className="flex items-end space-x-[12px] pt-[8px]">
          <div className="relative w-[200px] h-[14px] bg-brand-ink">
            <div className="absolute left-[50px] top-0 w-[2px] h-full bg-white" />
            <div className="absolute left-[100px] top-0 w-[2px] h-full bg-white" />
            <div className="absolute left-[150px] top-0 w-[2px] h-full bg-white" />
          </div>
          <span className="text-[18px] leading-none">200 px</span>
        </div>
      </div>
    </>
  )
}
